import { useMemo } from 'react';
import { PAGE_TYPE } from '../types/pages.type';
import { getPercentageByTotal } from '../utilis/common';
import useAICalculationContext from './useAICalculationContext';
import useAICardDataContext from './useAICardDataContext';
import useAIFacilityContext from './useAIFacilityContext';
import useAIChartAndPatientContext from './useAIChartAndPatientContext';
import { generateChartAnalysis } from '../utilis/chart-analytics';

/**
 * Custom hook for Community Transfer AI Chat Context
 * Dedicated hook for Community Transfer page with community-specific terminology and logic
 */
export const useAICommunityTransferChatContext = ({
  // Core data dependencies
  dbData,
  filterListData,
  patientList,
  activeFacilities,
  facilities,
  dynamicCards,
  
  // Filter and state dependencies
  filter,
  transferType,
  cardFilter,
  percentageBy,
  lockedTotalBy,
  specialComparisonSet,
  filterTotal,
  percentageAgainst,
  projectionDays,
  lastADTDate,
  
  // UI state dependencies
  communityTransferCards,
  rangesSet,
  isLayoutMode,
  
  // Function dependencies
  getPriorityNumber,
  lockedTotal,
  lockedByFacility,
  isCensusTotalLocked,
  facilityPercentage,
  
  // Metadata dependencies
  COMMUNITY_CUSTOM_CARD_DATA,
  tooltipsInfo,
  
  // Service dependencies
  aiCensusActionsService,
  aiReportActionsService,
  aiTransferActionsService,
  aiDialogActionsService,
  aiDateSuggestionsService,
  
  // Chart data dependencies
  chartData = null,
  chartAggregationType = 'unknown',
  currentChartFilter = null
}) => {
  // Use sub-hooks for different aspects of the context
  const calculationContext = useAICalculationContext({
    filter,
    transferType,
    projectionDays,
    cardFilter,
    percentageBy,
    lockedTotalBy,
    specialComparisonSet,
    filterTotal,
    percentageAgainst
  });

  const cardDataContext = useAICardDataContext({
    pageType: PAGE_TYPE.COMMUNITY_TRANSFER,
    filterListData,
    filter,
    transferType,
    projectionDays,
    cardFilter,
    percentageBy,
    lockedTotalBy,
    specialComparisonSet,
    filterTotal,
    percentageAgainst,
    dbData,
    getPriorityNumber,
    dynamicCards,
    patientList,
    HOSPITAL_CUSTOM_CARD_DATA: null,
    COMMUNITY_CUSTOM_CARD_DATA
  });

  const facilityContext = useAIFacilityContext({
    activeFacilities,
    patientList,
    facilities,
    dbData,
    percentageBy,
    lockedTotal,
    lockedTotalBy,
    pageType: PAGE_TYPE.COMMUNITY_TRANSFER
  });

  const chartAndPatientContext = useAIChartAndPatientContext();

  return useMemo(() => {
    // Calculate enhanced metrics for AI analysis
    const totalTransfers = dbData?.total || 0;
    const totalSafeDischarge = dbData?.totalSafeDischarge || 0;
    const totalSNF = dbData?.totalSNF || 0;
    const totalAMA = dbData?.totalAMA || 0;
    
    // Get active filters count
    const activeFiltersCount = cardFilter ? Object.values(cardFilter).filter(arr => Array.isArray(arr) && arr.length > 0).length : 0;
    
    // Calculate data availability
    const dataCategories = filterListData ? Object.keys(filterListData).filter(key => 
      Array.isArray(filterListData[key]) && filterListData[key].length > 0
    ) : [];

    // Get the correct percentage base (same logic as cards use)
    const currentPercentageBase = specialComparisonSet ? filterTotal : getPercentageByTotal(dbData);

    // IMPORTANT: Community Transfer Terminology definitions for AI
    const terminology = {
      safeDischarge: {
        label: "Safe Discharge",
        meaning: "Residents who were discharged safely to a community setting",
        description: "These are residents who had a safe discharge (isSafeDischarge: true). When users ask about 'safe discharge' or 'discharges', they mean safe discharges only.",
        keyTerms: ["safe discharge", "safely discharged", "community discharge", "successful discharge"],
        dataField: "totalSafeDischarge"
      },
      snf: {
        label: "SNF Transfers",
        meaning: "Skilled Nursing Facility transfers",
        description: "Residents who were transferred to a Skilled Nursing Facility for specialized care.",
        keyTerms: ["snf", "skilled nursing", "snf transfer", "nursing facility"],
        dataField: "totalSNF"
      },
      ama: {
        label: "AMA",
        meaning: "Against Medical Advice discharges",
        description: "Residents who left the facility Against Medical Advice.",
        keyTerms: ["ama", "against medical advice", "left ama"],
        dataField: "totalAMA"
      },
      total: {
        label: "Total Community Transfers",
        meaning: "All community transfers combined (Safe Discharge + SNF + AMA)",
        description: "The total count of all community transfers regardless of type.",
        keyTerms: ["total", "all transfers", "total community transfers", "all discharges"],
        dataField: "total"
      },
      important: "Community Transfer page tracks Safe Discharges, SNF Transfers, and AMA discharges. The total is the sum of all three types."
    };

    // Percentage Calculation Context (critical for accurate AI responses)
    const percentageContext = {
      currentMethod: percentageBy,
      currentPercentageBase,
      percentageAgainst,
      filterTotal,
      specialComparisonSet,
      isComparingAgainstAvgCensus: filterListData?.isComparingAgainstAvgCensus,
      lockedTotalBy,
      transferType,
      calculation: {
        description: `Percentages calculated using ${percentageBy} method`,
        baseValue: currentPercentageBase,
        explanation: specialComparisonSet 
          ? `Using filterTotal (${filterTotal}) as base because specialComparisonSet is true`
          : `Using percentageAgainst (${percentageAgainst}) as base from ${percentageBy} method`
      },
      priorityFiltering: {
        activePriorityFilters: cardFilter?.priorityData || [],
        totalActivePriorityFilters: cardFilter?.priorityData?.length || 0,
        firstPriorityCard: cardFilter?.priorityData?.[0]?.type || null,
        explanation: `When priority filters are applied, percentage bases change:
          - First priority filter uses mainNumPercentage or totalForPercentage
          - Subsequent filters use filtered resident count as denominator
          - This is why filtering affects percentage calculations across all cards`
      }
    };

    // Census Percentage Settings for AI interaction
    const censusSettings = (() => {
      try {
        const settings = aiCensusActionsService?.getCurrentPercentageSettings();
        return {
          currentMethod: settings?.currentMethod,
          customLabel: settings?.customLabel,
          customPercentages: settings?.customPercentages,
          availableMethods: settings?.availableMethods,
          censusData: settings?.censusData,
          explanations: {
            bedCapacity: aiCensusActionsService?.getPercentageExplanation('Bed Capacity'),
            censusAsOfNow: aiCensusActionsService?.getPercentageExplanation('census as of now'),
            censusAverage: aiCensusActionsService?.getPercentageExplanation('Average census of time frame selected')
          },
          actionSuggestions: aiCensusActionsService?.getAIActionSuggestions()
        };
      } catch (error) {
        return {
          currentMethod: percentageBy,
          available: false,
          error: 'Service not initialized'
        };
      }
    })();

    // Current state
    const currentState = {
      selectedTransferType: transferType,
      activeFiltersCount,
      availableDataCategories: dataCategories,
      selectedCards: communityTransferCards?.length || 0,
      totalCards: communityTransferCards?.length || 0,
      hasComparison: !!rangesSet,
      isLayoutMode,
      facilityInfo: {
        hasMultipleFacilities: activeFacilities?.length > 1,
        facilityCount: activeFacilities?.length || 0,
        activeFacilities: activeFacilities
      }
    };

    // Filter information
    const filters = {
      dateRange: {
        startDate: filter?.startDate,
        endDate: filter?.endDate,
        isCustomRange: filter?.startDate && filter?.endDate
      },
      facilities: {
        active: activeFacilities,
        count: activeFacilities?.length || 0
      },
      cardFilters: cardFilter,
      priorityFilters: cardFilter?.priorityData || []
    };

    // Data structure for AI analysis
    const dataStructure = {
      dbData: {
        available: !!dbData,
        recordCount: totalTransfers,
        hasSafeDischargeData: totalSafeDischarge > 0,
        hasSNFData: totalSNF > 0,
        hasAMAData: totalAMA > 0
      },
      filterListData: {
        available: !!filterListData,
        categories: dataCategories,
        totalCategories: dataCategories.length
      },
      patientList: {
        available: !!patientList?.list,
        count: patientList?.list?.length || 0,
        hasNinetyDaysData: !!(patientList?.ninetyDaysData?.length),
        hasCommunityData: !!(patientList?.communityData?.length),
        hasCustomTabs: !!(patientList?.customTabs?.length)
      }
    };

    // Available actions for AI suggestions
    const availableActions = {
      // Transfer Type Actions
      canChangeTransferType: true,
      canShowSafeDischarges: true,
      canShowUnsafeDischarges: true,
      canShowAllDischarges: true,
      canClearTransferFilter: true,
      
      // Filter Actions
      canApplyFilters: dataCategories.length > 0,
      canApplyCardFilters: true,
      canApplyMultipleFilters: true,
      canToggleSpecificItems: true,
      canResetFilters: activeFiltersCount > 0,
      canToggleAllFilters: true,
      canClearSpecificFilters: true,
      
      // Report Generation Actions
      canGenerateReports: totalTransfers > 0,
      canGeneratePdfReports: totalTransfers > 0,
      canGenerateExcelReports: totalTransfers > 0,
      canSetupAutomatedReports: totalTransfers > 0,
      canGenerateCardSpecificReports: dataCategories.length > 0,
      canGenerateFilteredReports: activeFiltersCount > 0,
      
      // Date and Time Actions
      canChangeDateRange: true,
      canSuggestDateRanges: true,
      canApplyDateFilters: true,
      
      // Percentage and Census Actions
      canChangePercentageMethod: true,
      canAddCustomPercentage: true,
      canExplainPercentageCalculations: true,
      canChangeCensusMethod: true,
      canLockTotals: true,
      canUnlockTotals: true,
      
      // Layout and Display Actions
      canToggleCards: communityTransferCards?.length > 0,
      canChangeLayout: true,
      canCompareData: true,
      canAnalyzeData: totalTransfers > 0,
      
      // Chart and Details Actions
      canViewChartData: totalTransfers > 0,
      canAnalyzeChartTrends: totalTransfers > 0,
      canViewResidentDetails: totalTransfers > 0,
      canAnalyzeDischargePatterns: totalTransfers > 0,
      canCompareTimePeriods: totalTransfers > 0,
      canViewDailyTrends: totalTransfers > 0,
      canViewWeeklyTrends: totalTransfers > 0,
      canViewMonthlyTrends: totalTransfers > 0,
      canViewYearlyTrends: totalTransfers > 0,
      
      // Advanced Actions
      canExplainDataRelationships: true,
      canProvideInsights: totalTransfers > 0,
      canSuggestOptimalFilters: dataCategories.length > 0,
      canExplainFilterImpact: activeFiltersCount > 0,
      canAnalyzeReadmissionRisk: totalTransfers > 0,
      canSuggestDischargeImprovements: totalTransfers > 0
    };

    // Report Actions Context
    const reportActions = (() => {
      try {
        const reportStatus = aiReportActionsService?.getCurrentReportStatus();
        const reportOptions = aiReportActionsService?.getAvailableReportOptions();
        return {
          currentStatus: reportStatus,
          availableCards: reportStatus?.availableCards,
          canGenerateReports: reportStatus?.canGenerateReports,
          reportTypes: reportStatus?.reportTypes,
          automatedTypes: reportStatus?.automatedTypes,
          suggestions: aiReportActionsService?.getAIActionSuggestions(),
          options: reportOptions
        };
      } catch (error) {
        return {
          available: false,
          error: 'Service not initialized'
        };
      }
    })();

    // Transfer Actions Context
    const transferActions = (() => {
      try {
        const transferStatus = aiTransferActionsService?.getCurrentTransferStatus();
        const availableTypes = aiTransferActionsService?.getAvailableTransferTypes();
        return {
          currentStatus: transferStatus,
          availableTypes: Object.keys(availableTypes || {}),
          currentType: transferStatus?.currentType,
          currentLabel: transferStatus?.currentLabel,
          currentCount: transferStatus?.currentCount,
          totalSafeDischarge: transferStatus?.totalSafeDischarge || totalSafeDischarge,
          totalSNF: transferStatus?.totalSNF || totalSNF,
          totalAMA: transferStatus?.totalAMA || totalAMA,
          totalAll: transferStatus?.totalAll || totalTransfers,
          percentages: transferStatus?.percentages,
          suggestions: aiTransferActionsService?.getAIActionSuggestions(),
          typeDetails: availableTypes
        };
      } catch (error) {
        return {
          available: false,
          error: 'Service not initialized'
        };
      }
    })();

    // Dialog Actions Context
    const dialogActions = (() => {
      try {
        const dialogStatus = aiDialogActionsService?.getCurrentDialogStatus();
        return {
          available: dialogStatus?.initialized,
          canOpenChartDialogs: dialogStatus?.canOpenChartDialogs,
          canOpenResidenceDialogs: dialogStatus?.canOpenResidenceDialogs,
          canOpenBuildingDialogs: dialogStatus?.canOpenBuildingDialogs,
          availableActions: dialogStatus?.availableActions,
          suggestions: [
            {
              type: 'chart_view',
              description: 'Open chart view for discharge trend analysis',
              triggers: ['chart', 'graph', 'trend', 'over time']
            },
            {
              type: 'residence_list',
              description: 'Show resident list for detailed discharge breakdown',
              triggers: ['residents', 'list', 'who are', 'breakdown']
            },
            {
              type: 'building_view',
              description: 'Show facility breakdown for community discharges',
              triggers: ['facilities', 'buildings']
            }
          ]
        };
      } catch (error) {
        return {
          available: false,
          error: 'Service not initialized'
        };
      }
    })();

    // Locked Functionality Context
    const lockedFunctionality = {
      currentState: {
        lockedTotal,
        lockedByFacility,
        lockedTotalBy,
        isCensusTotalLocked,
        facilityPercentage
      },
      isLocked: !!lockedTotal || !!lockedByFacility?.length,
      lockType: lockedTotalBy,
      lockDescription: lockedTotalBy === 'census' 
        ? 'Percentages locked to census data'
        : lockedTotalBy 
          ? `Percentages locked to ${lockedTotalBy} discharges`
          : 'No percentage lock applied',
      canLock: true,
      canUnlock: !!lockedTotal || !!lockedByFacility?.length,
      lockOptions: {
        byCensus: {
          type: 'census',
          description: 'Lock percentages to census data',
          available: true
        },
        byTransferType: {
          type: transferType,
          description: `Lock percentages to ${transferType || 'current'} discharges`,
          available: !!transferType
        },
        byFacility: {
          type: 'facility',
          description: 'Lock percentages by facility',
          available: activeFacilities?.length > 1
        }
      }
    };

    // Date Filtering Context
    const dateFiltering = {
      currentRange: filter,
      hasDateConstraints: false,
      canSuggestDates: true,
      validationInfo: aiDateSuggestionsService?.getCurrentValidationInfo()
    };

    // Filter Logic Explanation (community-specific)
    const filterLogic = {
      description: 'Community Transfer page uses filter logic from community-common.js',
      keyFunctions: {
        updateFilterListData: 'Main filter processing function that applies all card filters',
        priorityFilter: 'Handles priority-based filtering where first filter affects percentage calculations',
        checkOnlyOneFilter: 'Determines if only one filter type is active',
        updateFacilityPercentageTotal: 'Calculates facility-wise percentage totals'
      },
      filterFlow: `1. Apply card filters (discharge types, reasons, destinations, etc.)
          2. Calculate priority filter impact on percentage base
          3. Update locked totals if applicable
          4. Process facility-wise percentages
          5. Update filter totals and percentage bases
          6. Recalculate all card percentages based on new base`,
      prioritySystem: {
        explanation: `Priority filters change percentage calculation base:
          - First applied filter becomes the denominator for other card percentages
          - Example: If discharge type filter is applied first, other card percentages use that count as base`,
        currentPriority: cardFilter?.priorityData?.[0]?.type || null,
        priorityFilters: cardFilter?.priorityData || []
      },
      transferTypeImpact: {
        explanation: `Transfer type selection affects all calculations:
          - Safe: Shows only safe/planned discharges and filters all cards accordingly
          - Unsafe: Shows only unsafe/unplanned discharges and filters all cards accordingly
          - All: Shows combined data
          - None: Shows all data without transfer type filtering`,
        currentType: transferType,
        impact: transferType ? `Currently filtering to ${transferType} discharges only` : 'No transfer type filter applied'
      }
    };

    // Card tooltip information for AI understanding
    const cardTooltips = {
      communityTransferPage: tooltipsInfo?.communityTransferPage || {},
      availableCards: Object.keys(tooltipsInfo?.communityTransferPage || {}),
      cardDescriptions: Object.entries(tooltipsInfo?.communityTransferPage || {}).map(([cardKey, description]) => ({
        cardKey,
        description,
        cardType: cardKey.toLowerCase().replace(/[^a-z0-9]/g, '')
      }))
    };

    // CRITICAL: Chart Data Analytics for AI
    const chartAnalytics = (() => {
      if (!chartData || !Array.isArray(chartData) || chartData.length === 0) {
        return {
          available: false,
          message: 'No chart data available. Chart data will be available when viewing chart visualizations or analyzing discharge trends over time.',
          note: 'User can ask to view chart or trends to access this data'
        };
      }

      const analysis = generateChartAnalysis(chartData, chartAggregationType);
      
      return {
        available: true,
        aggregationType: chartAggregationType,
        currentFilter: currentChartFilter,
        analysis,
        
        // CRITICAL: Question-answering guides for AI (community-specific)
        questionAnsweringGuides: {
          'Which week had the most discharges?': {
            answer: `Use analysis.peak to find the period with highest value`,
            example: `The ${analysis.peak?.label} had the most community discharges with ${analysis.peak?.value} discharges`
          },
          'Are discharges trending up or down?': {
            answer: `Use analysis.trend.direction and analysis.trend.description`,
            example: `Community discharges show a ${analysis.trend?.direction} trend. ${analysis.trend?.description}`
          },
          'What happened after the peak discharge period?': {
            answer: `Use analysis.postPeakAnalysis for detailed post-peak behavior`,
            example: analysis.postPeakAnalysis?.available 
              ? `After the peak, ${analysis.postPeakAnalysis.immediateResponse.description}. ${analysis.postPeakAnalysis.analysis}`
              : 'Peak occurs at the end of the period'
          },
          'Show discharge pattern changes': {
            answer: `Use analysis.periodOverPeriodChanges for all period-to-period changes`,
            example: `Period-over-period changes available for ${analysis.periodOverPeriodChanges?.length} transitions`
          },
          'What is the average discharge rate?': {
            answer: `Use analysis.statistics.mean, median, and other stats`,
            example: `Average: ${analysis.statistics?.mean} discharges per ${chartAggregationType}, Median: ${analysis.statistics?.median}`
          },
          'Are there any discharge patterns?': {
            answer: `Use analysis.patterns to identify spikes, drops, and recurring patterns`,
            example: `${analysis.patterns?.description}. Spikes found: ${analysis.patterns?.spikes?.length}, Drops: ${analysis.patterns?.drops?.length}`
          }
        },
        
        // Quick reference data
        summary: {
          totalDataPoints: chartData.length,
          dateRange: `${analysis.dateRange?.start} to ${analysis.dateRange?.end}`,
          peakValue: analysis.peak?.value,
          peakPeriod: analysis.peak?.label,
          averageValue: analysis.statistics?.mean,
          medianValue: analysis.statistics?.median,
          trendDirection: analysis.trend?.direction,
          trendStrength: analysis.trend?.strength,
          overallChange: analysis.trend?.change,
          overallChangePercentage: analysis.trend?.changePercentage,
          largestIncrease: analysis.largestIncrease 
            ? `${analysis.largestIncrease.description}`
            : 'N/A',
          largestDecrease: analysis.largestDecrease 
            ? `${analysis.largestDecrease.description}`
            : 'N/A',
          volatility: analysis.summary?.volatility,
          dataQuality: analysis.summary?.dataQuality
        }
      };
    })();

    // Raw data for detailed analysis (limited for performance)
    const rawData = {
      dbData: dbData ? {
        total: dbData.total,
        totalSafeDischarge: dbData.totalSafeDischarge,
        totalSNF: dbData.totalSNF,
        totalAMA: dbData.totalAMA,
        // CRITICAL: Include ALL raw records for accurate cross-filtering (removing limit for accuracy)
        data: Array.isArray(patientList?.list) ? patientList.list : []
      } : null,
      sampleFilterData: filterListData ? Object.fromEntries(
        Object.entries(filterListData).slice(0, 5).map(([key, value]) => [
          key, Array.isArray(value) ? value.slice(0, 10) : value
        ])
      ) : null,
      // CRITICAL: Patient data for AI analysis
      patientData: (() => {
        if (!patientList?.list || !Array.isArray(patientList.list) || patientList.list.length === 0) {
          return null;
        }

        return {
          totalPatients: patientList.list.length,
          patients: patientList.list.map(patient => {
            const { __v, ...patientData } = patient;
            return {
              ...patientData,
              id: patient._id
            };
          })
        };
      })(),
      // CRITICAL: Current dashboard filters for AI cross-filter matching
      // This allows the backend to detect when AI query filters match the dashboard's current state
      // and use precomputed cardData for accurate counts (fixes AI vs dashboard count discrepancy)
      currentFilters: (() => {
        const currentFilters = {};
        const priorityData = cardFilter?.priorityData || [];

        // Map priority filter types to standardized filter keys used by AI
        priorityData.forEach(pf => {
          const filterType = pf.type;
          const selectedValues = pf.selectedValues || [];

          // Map filter types to standardized keys
          const typeMapping = {
            'dxData': 'diagnosis',
            'doctorData': 'doctor',
            'floorsData': 'floor',
            'insuranceData': 'insurance',
            'daysData': 'day_of_week',
            'returnsData': 'returned',
            'snfFacilityData': 'snf_facility',
            'assistedLivingData': 'assisted_living'
          };

          const filterKey = typeMapping[filterType] || filterType;

          if (selectedValues.length > 0) {
            // Extract labels from selected values for comparison
            currentFilters[filterKey] = selectedValues.map(v =>
              typeof v === 'object' ? (v.label || v.name || String(v)) : String(v)
            );
          }
        });

        return currentFilters;
      })()
    };

    // CRITICAL: Data Validation Rules for AI
    const dataValidationRules = {
      forExactCounts: {
        rule: "ALWAYS use item.actualValue or item.total for 'how many' questions",
        wrongFields: ["scaledValue", "projectedValue", "scaledPercentage"],
        correctFields: ["actualValue", "total", "value"],
        example: "For 'How many safe discharges?', use actualValue, NOT scaledValue"
      },
      forPercentages: {
        rule: "Use calculatedPercentage or scaledPercentage for percentage questions",
        correctFields: ["calculatedPercentage", "scaledPercentage", "percentage"]
      },
      forProjections: {
        rule: "Use projectedValue only for projection/forecast questions",
        correctFields: ["projectedValue", "projectedPercentage"]
      }
    };

    // CRITICAL: Common User Questions and How AI Should Respond (community-specific)
    const commonQuestions = {
      "How many safe discharges?": {
        answer: "Use dbData.totalSafeDischarge for the exact count",
        calculation: "dbData.totalSafeDischarge gives the EXACT count",
        dataSource: "metrics.totalSafeDischarge or rawData.dbData.totalSafeDischarge"
      },
      "How many SNF transfers?": {
        answer: "Use dbData.totalSNF for the exact count",
        calculation: "dbData.totalSNF gives the EXACT count",
        dataSource: "metrics.totalSNF or rawData.dbData.totalSNF"
      },
      "How many AMA discharges?": {
        answer: "Use dbData.totalAMA for the exact count",
        calculation: "dbData.totalAMA gives the EXACT count",
        dataSource: "metrics.totalAMA or rawData.dbData.totalAMA"
      },
      "How many total community transfers?": {
        answer: "Use dbData.total for the exact count (sum of Safe Discharge + SNF + AMA)",
        calculation: "dbData.total gives the EXACT total count",
        dataSource: "metrics.totalTransfers or rawData.dbData.total"
      },
      "Why did percentages change when I filtered?": {
        answer: "Priority filter system changes percentage denominators",
        explanation: "First filter becomes new base for other card percentages"
      },
      "What types of community transfers are tracked?": {
        answer: "Three types: Safe Discharges (residents safely discharged to community), SNF Transfers (to skilled nursing facilities), and AMA (Against Medical Advice)",
        breakdown: "Total = Safe Discharge + SNF + AMA"
      },
      "How are projections calculated?": {
        answer: "Use projectionPerMonth() function with current values and date range",
        formula: "For periods ≤30 days: (projectionDays * total) / actualDays"
      }
    };

    // Core metrics
    const metrics = {
      totalTransfers,
      totalSafeDischarge,
      totalSNF,
      totalAMA,
      safeDischargePercentage: totalTransfers > 0 ? ((totalSafeDischarge / totalTransfers) * 100).toFixed(1) : 0,
      snfPercentage: totalTransfers > 0 ? ((totalSNF / totalTransfers) * 100).toFixed(1) : 0,
      amaPercentage: totalTransfers > 0 ? ((totalAMA / totalTransfers) * 100).toFixed(1) : 0,
      filterTotal,
      percentageAgainst,
      projectionDays,
      lastADTDate
    };

    // Enhanced context with structured data
    return {
      pageType: PAGE_TYPE.COMMUNITY_TRANSFER,
      pageData: {
        // CRITICAL: Calculation Functions
        ...calculationContext,
        
        // CRITICAL: Card Data
        cardData: cardDataContext.cardData,
        currentPercentageBase: cardDataContext.currentPercentageBase,
        calculateCardPercentages: cardDataContext.calculateCardPercentages,
        
        // CRITICAL: Facility Analysis
        ...facilityContext,
        
        // Active facilities
        activeFacilities,
        
        // CRITICAL: Chart and Patient Details
        ...chartAndPatientContext,

        // Core metrics
        metrics,

        // IMPORTANT: Terminology definitions for AI understanding
        terminology,

        // Percentage Calculation Context
        percentageContext,

        // Census Percentage Settings
        censusSettings,
        
        // Current state
        currentState,
        
        // Filter information
        filters,
        
        // Data structure for AI analysis
        dataStructure,
        
        // Available actions for AI suggestions
        availableActions,

        // Report Actions Context
        reportActions,

        // Transfer Actions Context
        transferActions,

        // Dialog Actions Context
        dialogActions,

        // Locked Functionality Context
        lockedFunctionality,

        // Date Filtering Context
        dateFiltering,

        // Filter Logic Explanation
        filterLogic,
        
        // Card tooltip information
        cardTooltips,
        
        // Raw data for detailed analysis
        rawData,

        // CRITICAL: Data Validation Rules for AI
        dataValidationRules,

        // CRITICAL: Common User Questions
        commonQuestions,

        // CRITICAL: Card metadata
        cardMetadata: COMMUNITY_CUSTOM_CARD_DATA,

        // CRITICAL: Custom/Dynamic Cards
        customCards: {
          dynamicCards: dynamicCards || [],
          customTabs: patientList?.customTabs || []
        },

        // CRITICAL: Chart Analytics
        chartAnalytics,

        // CRITICAL: Chart interpretation instructions for AI
        chartInterpretation: {
          description: 'How to answer chart-related questions for community discharges',
          instructions: [
            '1. Check if chartAnalytics.available is true',
            '2. Use chartAnalytics.analysis for comprehensive data',
            '3. Reference chartAnalytics.questionAnsweringGuides for common questions',
            '4. Provide specific numbers, dates, and percentages from the analysis',
            '5. Always mention the aggregation type (daily, weekly, monthly)',
            '6. For trends, use analysis.trend for direction and strength',
            '7. For peaks, use analysis.peak with specific period and value',
            '8. For changes, use analysis.periodOverPeriodChanges for detailed breakdowns',
            '9. For patterns, use analysis.patterns for spikes, drops, and cycles',
            '10. For post-peak analysis, use analysis.postPeakAnalysis'
          ],
          bestPractices: [
            'Always include specific numbers and percentages',
            'Mention the time period (week of X, month of Y)',
            'Provide context about the overall discharge trend',
            'Compare to previous periods for context',
            'Explain the significance of changes in discharge patterns',
            'Use the exact period labels from the data'
          ]
        }
      },
      facilityIds: activeFacilities,
      timestamp: new Date().toISOString()
    };
  }, [
    // Core data dependencies
    dbData,
    filterListData,
    patientList,
    activeFacilities,
    dynamicCards,
    
    // Filter and state dependencies
    filter,
    transferType,
    cardFilter,
    percentageBy,
    lockedTotalBy,
    specialComparisonSet,
    filterTotal,
    percentageAgainst,
    projectionDays,
    lastADTDate,
    
    // UI state dependencies
    communityTransferCards,
    rangesSet,
    isLayoutMode,
    
    // Function dependencies
    lockedTotal,
    lockedByFacility,
    isCensusTotalLocked,
    facilityPercentage,
    
    // Metadata dependencies
    COMMUNITY_CUSTOM_CARD_DATA,
    tooltipsInfo,
    
    // Service dependencies
    aiCensusActionsService,
    aiReportActionsService,
    aiTransferActionsService,
    aiDialogActionsService,
    aiDateSuggestionsService,
    
    // Sub-hook dependencies
    calculationContext,
    cardDataContext.calculateCardPercentages,
    cardDataContext.cardData,
    cardDataContext.currentPercentageBase,
    chartAndPatientContext,
    facilityContext,
    
    // Chart data dependencies
    chartData,
    chartAggregationType,
    currentChartFilter
  ]);
};

export default useAICommunityTransferChatContext;

